import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTheme } from '../../contexts/ThemeContext';
import api from '../../lib/axios';
import Button from '../../components/ui/Button';
import { Card, CardContent } from '../../components/ui/Card';
import { FileText, Brain, Shield } from 'lucide-react';

const AssessmentWelcome = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { theme } = useTheme();
  const [assessment, setAssessment] = useState(null);
  const [error, setError] = useState('');
  // Live question counts, fetched from the same sources the questionnaires use,
  // so the intro never drifts when admins add/remove questions.
  const [counts, setCounts] = useState({ investment: null, personality: null });

  useEffect(() => {
    loadAssessment();
    loadCounts();
  }, [id]);

  const loadAssessment = async () => {
    try {
      const response = await api.get(`/assessments/access/${localStorage.getItem('assessmentToken')}`);
      setAssessment(response.data);
    } catch (error) {
      console.error('Failed to load assessment:', error);
    }
  };

  const loadCounts = async () => {
    try {
      const [investment, personality] = await Promise.all([
        api.get('/investment-goal-rules/questions'),
        api.get('/questions/personality'),
      ]);
      setCounts({
        investment: Array.isArray(investment.data) ? investment.data.length : null,
        personality: Array.isArray(personality.data) ? personality.data.length : null,
      });
    } catch (error) {
      console.error('Failed to load question counts:', error);
    }
  };

  const questionLabel = (n) => (n == null ? 'questions' : `${n} question${n === 1 ? '' : 's'}`);

  const handleContinue = async () => {
    try {
      await api.patch(`/assessments/${id}/status`, {
        status: 'in_progress',
        completion_method: 'client_direct'
      });
    } catch (err) {
      if (err.response?.status !== 409) {
        setError(err.response?.data?.error || 'Failed to start assessment. Please try again.');
        return;
      }
    }
    navigate(`/assessment/${id}/client-info`);
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-12" style={{ backgroundColor: theme.surface }}>
      <div className="max-w-2xl w-full">
        <Card>
          <CardContent className="pt-8 pb-8">
            <div className="text-center mb-8">
              <div className="w-20 h-20 mx-auto mb-4 rounded-full flex items-center justify-center" style={{ backgroundColor: `${theme.primary}20` }}>
                <FileText className="w-10 h-10" style={{ color: theme.primary }} />
              </div>
              <h1 className="text-3xl font-bold mb-2" style={{ color: theme.text }}>
                Investment Assessment
              </h1>
              <p className="text-lg" style={{ color: theme.textSecondary }}>
                Let's find the right investment strategy for you
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
              <div className="p-4 rounded-lg text-center" style={{ backgroundColor: theme.surface, border: `1px solid ${theme.border}` }}>
                <div className="w-12 h-12 mx-auto mb-3 rounded-full flex items-center justify-center" style={{ backgroundColor: '#3b82f620' }}>
                  <FileText className="w-6 h-6 text-blue-600 dark:text-blue-400" />
                </div>
                <h3 className="font-semibold mb-1" style={{ color: theme.text }}>Your Information</h3>
                <p className="text-sm" style={{ color: theme.textSecondary }}>Basic details</p>
              </div>

              <div className="p-4 rounded-lg text-center" style={{ backgroundColor: theme.surface, border: `1px solid ${theme.border}` }}>
                <div className="w-12 h-12 mx-auto mb-3 rounded-full flex items-center justify-center" style={{ backgroundColor: '#8b5cf620' }}>
                  <Brain className="w-6 h-6 text-purple-600 dark:text-purple-400" />
                </div>
                <h3 className="font-semibold mb-1" style={{ color: theme.text }}>Investment Goals</h3>
                <p className="text-sm" style={{ color: theme.textSecondary }}>{questionLabel(counts.investment)}</p>
              </div>

              <div className="p-4 rounded-lg text-center" style={{ backgroundColor: theme.surface, border: `1px solid ${theme.border}` }}>
                <div className="w-12 h-12 mx-auto mb-3 rounded-full flex items-center justify-center" style={{ backgroundColor: '#10b98120' }}>
                  <Shield className="w-6 h-6 text-green-600 dark:text-green-400" />
                </div>
                <h3 className="font-semibold mb-1" style={{ color: theme.text }}>Personality</h3>
                <p className="text-sm" style={{ color: theme.textSecondary }}>{questionLabel(counts.personality)}</p>
              </div>
            </div>

            <div className="space-y-4 mb-8">
              <div className="flex items-center p-3 rounded-lg" style={{ backgroundColor: theme.surface }}>
                <span className="text-green-500 mr-3">✓</span>
                <span className="text-sm" style={{ color: theme.textSecondary }}>Takes approximately 15-20 minutes</span>
              </div>
              <div className="flex items-center p-3 rounded-lg" style={{ backgroundColor: theme.surface }}>
                <span className="text-green-500 mr-3">✓</span>
                <span className="text-sm" style={{ color: theme.textSecondary }}>Your information is secure and confidential</span>
              </div>
              <div className="flex items-center p-3 rounded-lg" style={{ backgroundColor: theme.surface }}>
                <span className="text-green-500 mr-3">✓</span>
                <span className="text-sm" style={{ color: theme.textSecondary }}>You can save and return later if needed</span>
              </div>
            </div>

            {error && <p className="text-sm text-red-600 dark:text-red-400 mb-4 text-center">{error}</p>}

            <Button onClick={handleContinue} className="w-full" size="lg">
                Get Started
              </Button>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AssessmentWelcome;
